import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, AlertTriangle, CheckCircle } from 'lucide-react';

function NoteBox({ text }) {
  if (!text) return null;
  return (
    <div className="flex gap-2 mt-3 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-sm">
      <CheckCircle className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
      <span className="text-blue-700 dark:text-blue-300">{text}</span>
    </div>
  );
}

function WarningBox({ text }) {
  if (!text) return null;
  return (
    <div className="flex gap-2 mt-2 px-3 py-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-sm">
      <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
      <span className="text-amber-700 dark:text-amber-300">{text}</span>
    </div>
  );
}

function renderContentList(items) {
  const els = [];
  for (let i = 0; i < items.length; i++) {
    els.push(<li key={i}>{items[i]}</li>);
  }
  return els;
}

function renderDetailsList(items) {
  if (!items) return null;
  const els = [];
  for (let i = 0; i < items.length; i++) {
    els.push(
      <div key={i} className="flex gap-2 text-sm">
        <span className="font-semibold text-foreground shrink-0 min-w-[160px]">{items[i].label}:</span>
        <span className="text-muted-foreground">{items[i].desc}</span>
      </div>
    );
  }
  return <div className="space-y-2 mt-3">{els}</div>;
}

export function DocStepCard({ step, index }) {
  const [open, setOpen] = useState(false);

  const contentItems = renderContentList(step.content);
  const detailItems = renderDetailsList(step.details);

  return (
    <Card className="overflow-hidden">
      <button
        className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-muted/30 transition-colors cursor-pointer"
        onClick={() => setOpen(v => !v)}
        data-testid={`doc-step-toggle-${index}`}
      >
        <div className="flex items-center gap-4">
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-white text-sm font-bold shrink-0">{index + 1}</span>
          <div>
            <h3 className="font-semibold text-base">{step.title}</h3>
            <span className="text-xs text-muted-foreground">Page: {step.page}</span>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <CardContent className="pt-0 px-6 pb-5 border-t">
          <div className="pl-12 space-y-3">
            <ol className="list-decimal pl-4 space-y-1 text-sm text-foreground/90">
              {contentItems}
            </ol>
            {detailItems}
            <NoteBox text={step.note} />
            <WarningBox text={step.important} />
          </div>
        </CardContent>
      )}
    </Card>
  );
}
